import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { IconDownload } from '@tabler/icons-react'
import { isEmpty } from 'lodash'
import pluralize from 'pluralize'

import { Button } from '@/components/buttons/Button'
import { Checkbox } from '@/components/forms/Checkbox'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Loader } from '@/components/loaders/Loader'
import { toast } from '@/components/toasts/toast.helpers'
import {
  createAssetExport,
  previewAssetExport,
} from '@/reducers/callAssetExports/callAssetExports.actions'
import { clearPreview } from '@/reducers/callAssetExports/callAssetExports.redux'
import { BULK_ASSET_EXPORT_MAX_CALLS } from '@/utils/constants'

export const ASSET_TYPE = {
  audio: 'audio',
  transcript: 'transcript',
}

const ASSET_OPTIONS = [
  {
    value: ASSET_TYPE.audio,
    label: 'Audio',
    hint: 'The recording of each call. The bulk of the export size.',
  },
  {
    value: ASSET_TYPE.transcript,
    label: 'Transcripts',
    hint: 'The full text of each call, with speaker labels, one file per call.',
  },
]

// Both, because an export of one without the other is the exception rather
// than the norm, and unchecking is less work than hunting for the box you
// wanted. The CSV is no longer offered here at all: it is metadata about the
// calls, produced in seconds, while these are per-call artifacts produced in
// minutes -- so it lives on Explorer's own download button instead.
const DEFAULT_ASSET_TYPES = [ASSET_TYPE.audio, ASSET_TYPE.transcript]

const formatDuration = (totalMinutes) => {
  if (!totalMinutes) {
    return '—'
  }
  const hours = Math.floor(totalMinutes / 60)
  const minutes = Math.round(totalMinutes % 60)
  if (!hours) {
    return `${minutes} min`
  }
  return minutes ? `${hours} hr ${minutes} min` : `${hours} hr`
}

// Longer than the global 4s, because this toast carries the only immediate
// link to the job. Not much longer, though: the Toaster sets no closeButton,
// so a long default is an undismissable banner. The close button is what makes
// the duration a convenience rather than a wait.
const EXPORT_TOAST_DURATION_MS = 10000

/**
 * Starts a bulk export of the assets for the calls checked in Explorer.
 *
 * Calls only. Email, SMS and chat rows are a separate model with neither audio
 * nor a postcall transcript, so the caller gates this to the call channel.
 * @param {object} props
 * @param {number[]} [props.callIds] The checked calls
 * @param {number} props.organizationId The organization the export belongs to
 */
export const BulkAssetExport = ({ callIds = [], organizationId }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  // Explorer is open to agents, but exporting is a manager capability and the
  // API enforces it. Without this an agent sees the button, gets a 403, and
  // the toast's View link sends them to the dashboard.
  const hasCloudAccess = useSelector((state) => !!state.currentUser.balto_cloud_access)
  const [isOpen, setIsOpen] = useState(false)
  const [assetTypes, setAssetTypes] = useState(DEFAULT_ASSET_TYPES)

  const { preview, previewFailed, loading } = useSelector((state) => state.callAssetExports)

  const overLimit = callIds.length > BULK_ASSET_EXPORT_MAX_CALLS
  const assetTypesKey = assetTypes.join(',')
  const nothingSelected = isEmpty(assetTypes)

  // The preview depends on both the selection and the asset types, so it is
  // re-fetched when either moves. Cleared on close so a modal reopened over a
  // different selection never shows the previous counts, which a user would
  // read and believe before the new ones arrive.
  useEffect(() => {
    if (!isOpen || overLimit || nothingSelected) {
      return undefined
    }
    // Toggling checkboxes quickly fires overlapping previews, and without this
    // an older response can land last and overwrite the newer answer with
    // counts for a selection the user has already changed.
    const controller = new AbortController()
    dispatch(
      previewAssetExport({
        callIds,
        assetTypes,
        organizationId,
        signal: controller.signal,
      })
    )
    return () => controller.abort()
    // Keyed on the joined types rather than the array, which is rebuilt on
    // every render.
  }, [isOpen, callIds, assetTypesKey, organizationId, overLimit])

  const handleClose = () => {
    setIsOpen(false)
    setAssetTypes(DEFAULT_ASSET_TYPES)
    dispatch(clearPreview())
  }

  const toggleAssetType = (value) => {
    setAssetTypes((current) =>
      current.includes(value)
        ? current.filter((assetType) => assetType !== value)
        : [...current, value]
    )
  }

  const handleStart = async () => {
    const job = await dispatch(createAssetExport({ callIds, assetTypes, organizationId }))
    if (!job) {
      return
    }

    // The selection stays. The next thing someone does after starting an
    // export is usually another one over the same calls -- the transcripts
    // they forgot, or the asset type they picked wrongly -- and reselecting
    // hundreds of rows to correct that is far more work than dismissing a
    // selection they have finished with.
    handleClose()
    toast.success("Export started. We'll email you when it's ready.", {
      duration: EXPORT_TOAST_DURATION_MS,
      closeButton: true,
      // The message runs to two lines, so the action goes under it rather
      // than squeezing it into a narrow column beside the button.
      className: 'toast-action-below',
      action: { label: 'View download', onClick: () => history.push(`/exports/${job.id}`) },
    })
  }

  const withoutAudio =
    preview && assetTypes.includes(ASSET_TYPE.audio)
      ? Math.max(preview.call_count - preview.calls_with_audio, 0)
      : 0

  const summary = () => {
    if (overLimit) {
      return (
        <ErrorMessage
          content={`${callIds.length.toLocaleString()} calls selected. An export covers up to ${BULK_ASSET_EXPORT_MAX_CALLS.toLocaleString()} at a time — narrow the selection to continue.`}
        />
      )
    }
    if (nothingSelected) {
      return <p className="muted-text">Choose at least one kind of asset to export.</p>
    }
    if (previewFailed) {
      return (
        <ErrorMessage
          data-testid="bulk-asset-export-preview-error"
          content="Could not work out what this selection covers. Close and try again."
        />
      )
    }
    if (loading.preview || !preview) {
      return <Loader size="small" centered label="Working out what this covers…" />
    }
    return (
      <>
        {/* The duration is labelled rather than left bare: an unqualified
            time sitting between a call count and a size reads as an estimate
            of how long the export will take, which it is not. */}
        <p>
          <b>{preview.call_count.toLocaleString()}</b> {pluralize('call', preview.call_count)} ·{' '}
          <b>{formatDuration(preview.total_duration_minutes)}</b> of call time
          {/* Nothing at all for a transcript-only export, which has no size
              estimate -- the clause used to render as a dangling "about". */}
          {preview.estimated_size && (
            <>
              {' '}
              · about <b>{preview.estimated_size}</b>
            </>
          )}
        </p>
        {/* Shown before the user commits rather than explained afterwards:
            audio expires on a per-organization schedule and some calls never
            had it saved, so a shortfall here is normal and not an error. */}
        {withoutAudio > 0 && (
          <p className="muted-text">
            {withoutAudio.toLocaleString()} of these have no audio available and will be listed in
            the archive manifest instead.
          </p>
        )}
      </>
    )
  }

  if (!hasCloudAccess) {
    return null
  }

  return (
    <>
      <Button
        small
        icon={<IconDownload />}
        onClick={() => setIsOpen(true)}
        disabled={isEmpty(callIds)}
        data-testid="bulk-asset-export-button"
      >
        Download Call Assets
      </Button>

      <BasicModal
        show={isOpen}
        size="small"
        onClose={handleClose}
        title="Download Call Assets"
        footer={
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              brand
              onClick={handleStart}
              loading={loading.create}
              disabled={overLimit || nothingSelected || previewFailed || loading.create}
              data-testid="bulk-asset-export-start"
            >
              Start export
            </Button>
          </>
        }
      >
        <div className="flex-column medium-gap">
          <p className="muted-text">
            Assets for the {callIds.length.toLocaleString()} {pluralize('call', callIds.length)}{' '}
            checked in the table — the current selection only, not the whole filter set.
          </p>

          <div className="flex-column small-gap">
            {ASSET_OPTIONS.map(({ value, label, hint }) => (
              <Checkbox
                key={value}
                id={`bulk-asset-export-${value}`}
                className="radio-button radio-button--described"
                // The hint sits inside the label to share its card, so the
                // name is pointed at the title alone and the hint is read as
                // the description rather than run into the name.
                aria-labelledby={`bulk-asset-export-${value}-label`}
                aria-describedby={`bulk-asset-export-${value}-hint`}
                label={
                  <>
                    <span id={`bulk-asset-export-${value}-label`} className="balto-checkbox-title">
                      {label}
                    </span>
                    <span
                      id={`bulk-asset-export-${value}-hint`}
                      className="balto-checkbox-description"
                    >
                      {hint}
                    </span>
                  </>
                }
                checked={assetTypes.includes(value)}
                onChange={() => toggleAssetType(value)}
              />
            ))}
          </div>

          {summary()}

          {/* Set once here rather than discovered on the job page: a large
              export runs for a while and is delivered by email, so the user
              should know before they commit that this is not a download
              about to start. */}
          <p className="muted-text">
            Large exports take a while. We&apos;ll email you when yours is ready, and it stays
            available for a week.
          </p>
        </div>
      </BasicModal>
    </>
  )
}
